import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Stack,
  Divider,
  Collapse,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axiosInstance from '../services/axiosInstance';
import { formatDistanceToNow } from 'date-fns';

interface ApiKey {
  _id: string;
  key?: string;
  name: string;
  scopes: string[];
  isActive: boolean;
  createdAt: string;
  lastUsedAt?: string;
  expiresAt?: string;
  userId?: string;
  keyPreview?: string;
}

const AVAILABLE_SCOPES = [
  { value: 'assets:read', label: 'Assets: Read' },
  { value: 'assets:download', label: 'Assets: Download' },
  { value: 'assets:update', label: 'Assets: Update' },
  { value: 'collections:read', label: 'Collections: Read' },
  { value: 'categories:read', label: 'Categories: Read' },
  { value: 'templates:read', label: 'Templates: Read' }
];

const SCOPE_OPTIONS = AVAILABLE_SCOPES.map((scope) => scope.value);

function normalizeScopes(scopes: string[]): string[] {
  return Array.from(new Set(
    (scopes || [])
      .map((scope) => String(scope || '').trim())
      .filter(Boolean)
  ));
}

function toDateInputValue(value?: string): string {
  if (!value) return '';
  const normalized = String(value).trim();
  return normalized ? normalized.slice(0, 10) : '';
}

const ApiKeyManagement: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newKeyDialog, setNewKeyDialog] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expirationDate, setExpirationDate] = useState<string>('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState<string | null>(null);
  const [editKeyDialog, setEditKeyDialog] = useState(false);
  const [editingKeyId, setEditingKeyId] = useState<string | null>(null);
  const [editingKeyName, setEditingKeyName] = useState('');
  const [editingScopes, setEditingScopes] = useState<string[]>([]);
  const [editingExpirationDate, setEditingExpirationDate] = useState('');
  const [editingIsActive, setEditingIsActive] = useState(true);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get('/api/apikeys');
      setApiKeys(response.data);
    } catch (err: any) {
      setError('Failed to load API keys: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!keyName.trim()) {
      setError('API key name is required');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post('/api/apikeys', {
        name: keyName.trim(),
        scopes: normalizeScopes(selectedScopes),
        expiresAt: expirationDate ? new Date(expirationDate).toISOString() : undefined
      });

      setApiKeys([response.data, ...apiKeys]);
      setCreatedKey(response.data.key);
      setSuccessMessage('API key created successfully!');
      setNewKeyDialog(false);
      
      // Reset form state
      setKeyName('');
      setSelectedScopes([]);
      setExpirationDate('');
    } catch (err: any) {
      setError('Failed to create API key: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (apiKey: ApiKey) => {
    setError(null);
    setEditingKeyId(apiKey._id);
    setEditingKeyName(apiKey.name);
    setEditingScopes(normalizeScopes(apiKey.scopes || []));
    setEditingExpirationDate(toDateInputValue(apiKey.expiresAt));
    setEditingIsActive(apiKey.isActive);
    setEditKeyDialog(true);
  };

  const closeEditDialog = () => {
    setEditKeyDialog(false);
    setEditingKeyId(null);
    setEditingKeyName('');
    setEditingScopes([]);
    setEditingExpirationDate('');
    setEditingIsActive(true);
  };

  const handleSaveKeyChanges = async () => {
    if (!editingKeyId) return;
    if (!editingKeyName.trim()) {
      setError('API key name is required');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.patch(`/api/apikeys/${editingKeyId}`, {
        name: editingKeyName.trim(),
        scopes: normalizeScopes(editingScopes),
        isActive: editingIsActive,
        expiresAt: editingExpirationDate ? new Date(editingExpirationDate).toISOString() : null
      });

      setApiKeys(apiKeys.map((key) => (
        key._id === editingKeyId ? { ...key, ...response.data } : key
      )));
      setSuccessMessage('API key updated successfully');
      closeEditDialog();
    } catch (err: any) {
      setError('Failed to update API key: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleKeyStatus = async (keyId: string, currentStatus: boolean) => {
    try {
      setLoading(true);
      await axiosInstance.patch(`/api/apikeys/${keyId}`, {
        isActive: !currentStatus
      });
      
      // Update the key status in local state
      setApiKeys(apiKeys.map(key => 
        key._id === keyId ? { ...key, isActive: !currentStatus } : key
      ));
      
      setSuccessMessage(`API key ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err: any) {
      setError('Failed to update API key: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    try {
      setLoading(true);
      await axiosInstance.delete(`/api/apikeys/${keyId}`);
      
      // Remove the key from local state
      setApiKeys(apiKeys.filter(key => key._id !== keyId));
      
      setSuccessMessage('API key deleted successfully');
      setConfirmDeleteDialog(null);
    } catch (err: any) {
      setError('Failed to delete API key: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => setSuccessMessage('API key copied to clipboard'))
      .catch(() => setError('Failed to copy API key to clipboard'));
  };

  const toggleKeyDetails = (keyId: string) => {
    setExpandedKey(expandedKey === keyId ? null : keyId);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">API Key Management</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchApiKeys}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNewKeyDialog(true)}
          >
            Create API Key
          </Button>
        </Box>
      </Box>

      {loading && <CircularProgress />}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && apiKeys.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            No API keys found. Create your first API key to enable external access to the DAM system.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys.map((apiKey) => (
                <React.Fragment key={apiKey._id}>
                  <TableRow>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleKeyDetails(apiKey._id)}
                        >
                          {expandedKey === apiKey._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Typography>{apiKey.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {apiKey.keyPreview || (apiKey.key ? apiKey.key.substring(0, 8) + '...' + apiKey.key.substring(apiKey.key.length - 4) : '')}
                        </Typography>
                        {createdKey && apiKey.key === createdKey && (
                          <>
                            <IconButton 
                              size="small" 
                              onClick={() => copyToClipboard(createdKey)}
                              sx={{ ml: 1 }}
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                            <Chip 
                              label="New" 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 1 }} 
                            />
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={apiKey.isActive}
                            onChange={() => handleToggleKeyStatus(apiKey._id, apiKey.isActive)}
                            size="small"
                          />
                        }
                        label={apiKey.isActive ? "Active" : "Inactive"}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(apiKey.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Delete API Key">
                        <IconButton 
                          color="error" 
                          onClick={() => setConfirmDeleteDialog(apiKey._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit API Key">
                        <IconButton
                          color="primary"
                          onClick={() => openEditDialog(apiKey)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ p: 0, borderBottom: 'none' }}>
                      <Collapse in={expandedKey === apiKey._id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                          <Typography variant="subtitle2">Details</Typography>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                <strong>Created:</strong> {new Date(apiKey.createdAt).toLocaleString()}
                                {' '}({formatDistanceToNow(new Date(apiKey.createdAt), { addSuffix: true })})
                              </Typography>
                              {apiKey.lastUsedAt && (
                                <Typography variant="body2">
                                  <strong>Last used:</strong> {new Date(apiKey.lastUsedAt).toLocaleString()}
                                  {' '}({formatDistanceToNow(new Date(apiKey.lastUsedAt), { addSuffix: true })})
                                </Typography>
                              )}
                              {apiKey.expiresAt && (
                                <Typography variant="body2">
                                  <strong>Expires:</strong> {new Date(apiKey.expiresAt).toLocaleString()}
                                  {' '}({formatDistanceToNow(new Date(apiKey.expiresAt), { addSuffix: true })})
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2"><strong>Scopes:</strong></Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {apiKey.scopes.length > 0 ? (
                                  apiKey.scopes.map((scope) => (
                                    <Chip 
                                      key={scope} 
                                      label={scope} 
                                      size="small" 
                                      variant="outlined" 
                                    />
                                  ))
                                ) : (
                                  <Typography variant="body2" color="text.secondary">No specific scopes (full access)</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create New API Key Dialog */}
      <Dialog 
        open={newKeyDialog} 
        onClose={() => setNewKeyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="API Key Name"
            type="text"
            fullWidth
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            required
            placeholder="e.g., InDesign Plugin, PowerPoint Add-in"
            helperText="Give your API key a descriptive name to identify its purpose"
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            freeSolo
            options={SCOPE_OPTIONS}
            value={selectedScopes}
            onChange={(_event, value) => setSelectedScopes(normalizeScopes(value.map((item) => String(item))))}
            filterSelectedOptions
            sx={{ mb: 2 }}
            renderTags={(value, getTagProps) => (
              value.map((scope, index) => (
                <Chip {...getTagProps({ index })} key={`${scope}-${index}`} label={scope} size="small" />
              ))
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Access Scopes"
                placeholder="assets:update"
                helperText="You can pick suggested scopes or type your own custom scope values."
              />
            )}
          />

          <TextField
            margin="dense"
            label="Expiration Date (Optional)"
            type="date"
            fullWidth
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="If left empty, the API key will not expire"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewKeyDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateKey} 
            variant="contained" 
            disabled={!keyName.trim() || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Create API Key'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editKeyDialog}
        onClose={closeEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="API Key Name"
            type="text"
            fullWidth
            value={editingKeyName}
            onChange={(e) => setEditingKeyName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />

          <Autocomplete
            multiple
            freeSolo
            options={Array.from(new Set([...SCOPE_OPTIONS, ...editingScopes]))}
            value={editingScopes}
            onChange={(_event, value) => setEditingScopes(normalizeScopes(value.map((item) => String(item))))}
            filterSelectedOptions
            sx={{ mb: 2 }}
            renderTags={(value, getTagProps) => (
              value.map((scope, index) => (
                <Chip {...getTagProps({ index })} key={`${scope}-${index}`} label={scope} size="small" />
              ))
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Access Scopes"
                placeholder="assets:update"
                helperText="Edit scopes directly here. You can also type custom scope strings if needed."
              />
            )}
          />

          <TextField
            margin="dense"
            label="Expiration Date (Optional)"
            type="date"
            fullWidth
            value={editingExpirationDate}
            onChange={(e) => setEditingExpirationDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Clear the date if the key should not expire"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={editingIsActive}
                onChange={() => setEditingIsActive((current) => !current)}
              />
            }
            label={editingIsActive ? 'Active' : 'Inactive'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button
            onClick={handleSaveKeyChanges}
            variant="contained"
            disabled={!editingKeyName.trim() || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDeleteDialog}
        onClose={() => setConfirmDeleteDialog(null)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this API key? This action cannot be undone and any applications using this key will lose access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => confirmDeleteDialog && handleDeleteKey(confirmDeleteDialog)} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created Key Dialog */}
      <Dialog
        open={!!createdKey}
        onClose={() => setCreatedKey(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time your full API key will be displayed. Please copy it now and store it securely.
          </Alert>
          
          <Paper
            sx={{
              p: 2,
              bgcolor: '#f5f5f5',
              fontFamily: 'monospace',
              fontSize: '1rem',
              wordBreak: 'break-all',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ flexGrow: 1 }}>{createdKey}</Box>
            <IconButton onClick={() => createdKey && copyToClipboard(createdKey)}>
              <FileCopyIcon />
            </IconButton>
          </Paper>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Integration Instructions</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            To use this API key with your InDesign plugin or PowerPoint add-in:
          </Typography>
          
          <Box component="pre" sx={{ 
            p: 2, 
            bgcolor: '#f5f5f5', 
            overflowX: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            borderRadius: 1
          }}>
{`// Example API request
fetch('${window.location.origin}/api/assets', {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': '${createdKey}'
  }
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedKey(null)} variant="contained">
            I've Copied My API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success" 
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApiKeyManagement; 